import { Control, Controller, FieldErrors, FieldPath, FieldValues, get } from 'react-hook-form';

import { Chips } from 'primereact/chips';
import { classNames } from 'primereact/utils';

import styles from './FormChips.module.css';

export type FormChipsProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  className?: string;
  inputClassName?: string;
  control: Control<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
  field: TName;
  label: string;
  disabled?: boolean;
  placeholder?: string;
  required?: boolean;
  separator?: string;
  max?: number;
};

const FormChips = <TFieldValues extends FieldValues>({ ...props }: FormChipsProps<TFieldValues>): JSX.Element => {
  const {
    className,
    inputClassName,
    control,
    field,
    errors,
    label,
    disabled,
    placeholder,
    required,
    separator,
    max,
  } = props;

  const errorTemplate = () => {
    const error = get(errors, field);
    return error && <small className="p-error">{error?.message as string}</small>;
  };

  return (
    <div className={classNames(className ?? 'field', styles.chipsContainer)}>
      <Controller
        name={field}
        control={control}
        render={({ field, fieldState }) => (
          <>
            {label && (
              <label htmlFor={field.name} className={classNames('font-medium', { 'p-error': fieldState.error, disabled })}>
                {label}
                {required && <span className="text-red-500 ml-1">*</span>}
              </label>
            )}
            <Chips
              id={field.name}
              value={Array.isArray(field.value) ? field.value : []}
              onChange={(e) => field.onChange(e.value ?? [])}
              className={classNames(inputClassName, { 'p-invalid': fieldState.error, disabled })}
              disabled={disabled}
              placeholder={Array.isArray(field.value) && field.value.length > 0 ? '' : placeholder}
              separator={separator || ','}
              max={max}
              style={{ fontSize: '0.875rem' }}
              pt={{
                container: {
                  style: { 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: '0.25rem', 
                    alignItems: 'center',
                    padding: '4.25px 8px'
                  }
                },
                token: {
                  style: { 
                    fontSize: '0.75rem', 
                    margin: 0,
                    backgroundColor: '#3B82F6',
                    color: 'white',
                    borderRadius: '0.375rem',
                    padding: '0.25rem 0.5rem',
                    fontWeight: '500'
                  }
                },
                removeTokenIcon: {
                  style: {
                    color: 'white',
                    marginLeft: '0.25rem'
                  }
                },
                inputToken: {
                  className: 'chips-input-token'
                },
                input: {
                  className: 'chips-input-field'
                }
              }}
            />
          </>
        )}
      />
      {errorTemplate()}
    </div>
  );
};

export default FormChips;

